import { useEffect, useContext, useRef } from 'react'
import YouTube from 'react-youtube'

// Context
import { ModalContext } from '../../context/ModalContext'
import { ModalGalleryContext } from '../../context/ModalGalleryContext'
import { NavigationContext } from '../../context/NavigationContext'
import CookiePlaceholder from '../CookiePlaceholder/CookiePlaceholder'

// Internal components
import Svg from '../Svg/Svg'

const loadedClass = 'is-loaded'

const ModalGallery = () => {
  // Context
  const modalContext = useContext(ModalContext)
  const modalGalleryContext = useContext(ModalGalleryContext)
  const navigationContext = useContext(NavigationContext)

  // Refs
  const refPrevButton = useRef()
  const refNextButton = useRef()

  const embedContainerRef = useRef()

  // Sets up the modal gallery context values
  useEffect(() => {
    modalGalleryContext.setCurrentValues(modalContext)
  }, [])

  // state adjustments to show or hide buttons and transitions.
  useEffect(() => {
    modalContext.setStateClass(`${loadedClass} is-transitioning`)
    // this hides the previous and next buttons when those can not be used.
    if (modalGalleryContext.currentIndex <= 0) refPrevButton.current.classList.add('is-hidden')
    if (modalGalleryContext.currentIndex > 0) refPrevButton.current.classList.remove('is-hidden')
    if (
      modalGalleryContext.currentIndex >= modalGalleryContext.videoList.length - 1 ||
      modalGalleryContext.videoList.length <= 1
    )
      refNextButton.current.classList.add('is-hidden')
    if (
      modalGalleryContext.currentIndex < modalGalleryContext.videoList.length - 1 &&
      modalGalleryContext.videoList.length > 1
    )
      refNextButton.current.classList.remove('is-hidden')
  }, [modalGalleryContext.currentIndex, modalGalleryContext.videoList])

  // Sets a timeout for the transition.
  useEffect(() => {
    if (modalGalleryContext.currentId) {
      const timer = setTimeout(() => modalContext.setStateClass(`${loadedClass} has-transitioned`), 500)
      return () => clearTimeout(timer)
    }
  }, [modalGalleryContext.currentId])

  // CLICK HANDLERS
  const setPlayerIndex = (index) => {
    modalGalleryContext.setCurrentIndex(index)
    modalGalleryContext.setCurrentId(modalGalleryContext.videoList[index]?.youtubeId)
  }

  return (
    <div className="ModalGallery">
      <div className="ModalGallery-inner">
        <div className="ModalGallery-section">
          <div className="ModalGallery-embedWrapper">
            <div className="ModalGallery-embedContainer" ref={embedContainerRef}>
              {modalGalleryContext.currentId && navigationContext.hasConsent('youtube') && (
                <YouTube
                  videoId={modalGalleryContext.currentId}
                  className="ModalGallery-embed"
                  opts={{ playerVars: { rel: 0, autoplay: 1 } }}
                />
              )}
              <CookiePlaceholder
                modifiers={['CookiePlaceholder--modalGallery']}
                placeholderWrapperRef={embedContainerRef}
              />
            </div>
          </div>
          <div className="ModalGallery-navigation">
            <div
              className="ModalGallery-prev"
              onClick={() => setPlayerIndex(modalGalleryContext.currentIndex - 1)}
              ref={refPrevButton}
            >
              <Svg id="icon-thin-arrow-left" modifiers={['ModalGallery-svgPrev']} />
            </div>
            <div
              className="ModalGallery-next"
              onClick={() => setPlayerIndex(modalGalleryContext.currentIndex + 1)}
              ref={refNextButton}
            >
              <Svg id="icon-thin-arrow-right" modifiers={['ModalGallery-svgNext']} />
            </div>
          </div>
          {modalGalleryContext && (
            <div className="ModalGallery-caption">
              <span className="ModalGallery-captionText">
                <Svg id="icon-video" modifiers={['ModalGallery-svgCaption']} />
                {modalGalleryContext.videoList[modalGalleryContext.currentIndex]?.youtubeTitle}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ModalGallery
